import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { ScrollArea } from '../components/ui/scroll-area';
import { Clock, AlertTriangle, Tag, Package, RefreshCw, TrendingDown, DollarSign, Calendar } from 'lucide-react';
import { differenceInDays, format, parseISO } from 'date-fns';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function ExpiryDashboard() {
  const [medicines, setMedicines] = useState([]);
  const [expiryDays, setExpiryDays] = useState(60);
  const [loading, setLoading] = useState(false);
  const [actionModal, setActionModal] = useState(null);
  const [discountPercent, setDiscountPercent] = useState('10');
  const [returnNote, setReturnNote] = useState('');

  const fetchExpiringMedicines = useCallback(async function() {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/medicines`);
      // Filter and process for near-expiry items
      const nearExpiry = [];
      const today = new Date();
      
      for (let i = 0; i < res.data.length; i++) {
        const med = res.data[i];
        if (med.batches && med.batches.length > 0) {
          for (let j = 0; j < med.batches.length; j++) {
            const batch = med.batches[j];
            if (batch.quantity > 0) {
              const expiryDate = parseISO(batch.expiry_date);
              const daysUntilExpiry = differenceInDays(expiryDate, today);
              
              if (daysUntilExpiry <= expiryDays && daysUntilExpiry > -30) {
                nearExpiry.push({
                  ...med,
                  batch_id: batch.batch_id,
                  expiry_date: batch.expiry_date,
                  batch_quantity: batch.quantity,
                  days_until_expiry: daysUntilExpiry,
                  purchase_price: batch.purchase_price || 0,
                  potential_loss: (batch.quantity * (med.mrp || 0)),
                  status: daysUntilExpiry <= 0 ? 'expired' : daysUntilExpiry <= 15 ? 'critical' : daysUntilExpiry <= 30 ? 'warning' : 'near'
                });
              }
            }
          }
        }
      }
      
      // Sort by days until expiry (most urgent first)
      nearExpiry.sort(function(a, b) { return a.days_until_expiry - b.days_until_expiry; });
      setMedicines(nearExpiry);
    } catch (error) {
      toast.error('Failed to fetch medicines');
    } finally {
      setLoading(false);
    }
  }, [expiryDays]);

  useEffect(function() {
    fetchExpiringMedicines();
  }, [fetchExpiringMedicines]);

  function openActionModal(med, action) {
    setActionModal({ medicine: med, action: action });
    setDiscountPercent('10');
    setReturnNote('');
  }

  async function applyDiscount() {
    if (!actionModal) return;
    const med = actionModal.medicine;
    const discount = parseFloat(discountPercent);
    
    if (discount < 0 || discount > 100) {
      toast.error('Invalid discount percentage');
      return;
    }

    // Calculate discounted price
    const discountedPrice = med.mrp * (1 - discount / 100);
    
    toast.success(`Discount applied! New price: ₹${discountedPrice.toFixed(2)} (${discount}% off)`);
    toast.info(`Use this price when billing ${med.brand_name} (Batch: ${med.batch_id})`);
    setActionModal(null);
  }

  async function markForReturn() {
    if (!actionModal) return;
    const med = actionModal.medicine;
    
    // Log the return request (in a real app, this would create a return request record)
    toast.success(`Return request logged for ${med.brand_name}`);
    toast.info(`Batch ${med.batch_id} - ${med.batch_quantity} units marked for distributor return`);
    
    if (returnNote) {
      toast.info(`Note: ${returnNote}`);
    }
    
    setActionModal(null);
  }

  function getTotalPotentialLoss() {
    let total = 0;
    for (let i = 0; i < medicines.length; i++) {
      total += medicines[i].potential_loss;
    }
    return total;
  }

  function countByStatus(status) {
    let count = 0;
    for (let i = 0; i < medicines.length; i++) {
      if (medicines[i].status === status) count++;
    }
    return count;
  }

  function getStatusBadge(status, days) {
    if (status === 'expired') return <Badge className="bg-red-600 text-white">EXPIRED</Badge>;
    if (status === 'critical') return <Badge className="bg-red-500 text-white">{days} days</Badge>;
    if (status === 'warning') return <Badge className="bg-amber-500 text-white">{days} days</Badge>;
    return <Badge className="bg-yellow-400 text-black">{days} days</Badge>;
  }

  function renderMedicineCards() {
    if (medicines.length === 0) {
      return (
        <div className="col-span-full text-center py-12 text-slate-400">
          <Clock className="w-16 h-16 mx-auto mb-4 opacity-50" />
          <h3 className="text-lg font-medium mb-2">No Near-Expiry Items</h3>
          <p>All medicines are well within their expiry dates.</p>
        </div>
      );
    }

    const cards = [];
    for (let i = 0; i < medicines.length; i++) {
      const med = medicines[i];
      cards.push(
        <Card key={`${med.id}-${med.batch_id}`} className={`
          border-l-4 
          ${med.status === 'expired' ? 'border-l-red-600 bg-red-50' : ''}
          ${med.status === 'critical' ? 'border-l-red-500 bg-red-50' : ''}
          ${med.status === 'warning' ? 'border-l-amber-500 bg-amber-50' : ''}
          ${med.status === 'near' ? 'border-l-yellow-400 bg-yellow-50' : ''}
        `}>
          <CardContent className="p-4">
            <div className="flex justify-between items-start mb-3">
              <div>
                <h3 className="font-semibold text-navy-900">{med.brand_name}</h3>
                <p className="text-sm text-slate-500">{med.salt_composition}</p>
                <p className="text-xs text-slate-400 mt-1">Batch: {med.batch_id}</p>
              </div>
              {getStatusBadge(med.status, med.days_until_expiry)}
            </div>
            
            <div className="grid grid-cols-3 gap-2 text-center mb-3">
              <div className="bg-white bg-opacity-60 rounded p-2">
                <p className="text-xs text-slate-500">Qty</p>
                <p className="font-bold">{med.batch_quantity}</p>
              </div>
              <div className="bg-white bg-opacity-60 rounded p-2">
                <p className="text-xs text-slate-500">MRP</p>
                <p className="font-bold">₹{med.mrp}</p>
              </div>
              <div className="bg-white bg-opacity-60 rounded p-2">
                <p className="text-xs text-slate-500">At Risk</p>
                <p className="font-bold text-red-600">₹{med.potential_loss}</p>
              </div>
            </div>
            
            <div className="flex items-center justify-between text-sm mb-3">
              <div className="flex items-center gap-1 text-slate-600">
                <Calendar className="w-4 h-4" />
                <span>Expires: {format(parseISO(med.expiry_date), 'dd MMM yyyy')}</span>
              </div>
            </div>
            
            <div className="flex gap-2">
              <Button 
                size="sm" 
                variant="outline" 
                className="flex-1 text-amber-700 border-amber-300 hover:bg-amber-50"
                onClick={function() { openActionModal(med, 'discount'); }}
              >
                <Tag className="w-4 h-4 mr-1" /> Apply Discount
              </Button>
              <Button 
                size="sm" 
                variant="outline" 
                className="flex-1 text-blue-700 border-blue-300 hover:bg-blue-50"
                onClick={function() { openActionModal(med, 'return'); }}
              >
                <RefreshCw className="w-4 h-4 mr-1" /> Mark Return
              </Button>
            </div>
          </CardContent>
        </Card>
      );
    }
    return cards;
  }

  return (
    <div className="p-4 lg:p-6">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-heading font-bold text-navy-900">Expiry & Liquidation</h1>
          <p className="text-slate-500 text-sm">Manage near-expiry stock, apply discounts, or mark for return</p>
        </div>
        <div className="flex items-center gap-3">
          <Label className="text-sm">Show expiring in:</Label>
          <Select value={expiryDays.toString()} onValueChange={function(v) { setExpiryDays(parseInt(v)); }}>
            <SelectTrigger className="w-32">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="15">15 days</SelectItem>
              <SelectItem value="30">30 days</SelectItem>
              <SelectItem value="60">60 days</SelectItem>
              <SelectItem value="90">90 days</SelectItem>
            </SelectContent>
          </Select>
          <Button variant="outline" onClick={fetchExpiringMedicines} disabled={loading}>
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center">
                <AlertTriangle className="w-5 h-5 text-red-600" />
              </div>
              <div>
                <p className="text-sm text-slate-500">Expired</p>
                <p className="text-xl font-bold text-red-600">{countByStatus('expired')}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center">
                <Clock className="w-5 h-5 text-red-500" />
              </div>
              <div>
                <p className="text-sm text-slate-500">Critical (&lt;15d)</p>
                <p className="text-xl font-bold text-red-500">{countByStatus('critical')}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center">
                <Clock className="w-5 h-5 text-amber-500" />
              </div>
              <div>
                <p className="text-sm text-slate-500">Warning (&lt;30d)</p>
                <p className="text-xl font-bold text-amber-500">{countByStatus('warning')}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-yellow-100 rounded-lg flex items-center justify-center">
                <Package className="w-5 h-5 text-yellow-600" />
              </div>
              <div>
                <p className="text-sm text-slate-500">Near (&lt;{expiryDays}d)</p>
                <p className="text-xl font-bold text-yellow-600">{countByStatus('near')}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-red-50 border-red-200">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-red-200 rounded-lg flex items-center justify-center">
                <TrendingDown className="w-5 h-5 text-red-700" />
              </div>
              <div>
                <p className="text-sm text-red-600">Potential Loss</p>
                <p className="text-xl font-bold text-red-700">₹{getTotalPotentialLoss().toLocaleString()}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Medicine Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {renderMedicineCards()}
      </div>

      {/* Action Modal */}
      <Dialog open={!!actionModal} onOpenChange={function() { setActionModal(null); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {actionModal?.action === 'discount' ? 'Apply Clearance Discount' : 'Mark for Distributor Return'}
            </DialogTitle>
          </DialogHeader>
          
          {actionModal && (
            <div className="space-y-4">
              <Card className="bg-slate-50">
                <CardContent className="p-4">
                  <div className="flex justify-between">
                    <div>
                      <h3 className="font-semibold">{actionModal.medicine.brand_name}</h3>
                      <p className="text-sm text-slate-500">Batch: {actionModal.medicine.batch_id}</p>
                    </div>
                    <div className="text-right">
                      <p className="font-mono">₹{actionModal.medicine.mrp}</p>
                      <p className="text-sm text-slate-500">Qty: {actionModal.medicine.batch_quantity}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {actionModal.action === 'discount' ? (
                <>
                  <div>
                    <Label>Discount Percentage</Label>
                    <div className="flex gap-2 mt-1">
                      <Input
                        type="number"
                        min="0"
                        max="100"
                        value={discountPercent}
                        onChange={function(e) { setDiscountPercent(e.target.value); }}
                      />
                      <span className="flex items-center text-lg">%</span>
                    </div>
                  </div>
                  <div className="p-3 bg-mint-50 rounded-lg">
                    <div className="flex justify-between">
                      <span>Original Price:</span>
                      <span className="font-mono">₹{actionModal.medicine.mrp}</span>
                    </div>
                    <div className="flex justify-between text-mint-700 font-bold">
                      <span>Discounted Price:</span>
                      <span className="font-mono">
                        ₹{(actionModal.medicine.mrp * (1 - parseFloat(discountPercent || 0) / 100)).toFixed(2)}
                      </span>
                    </div>
                  </div>
                  <Button className="w-full bg-amber-500 hover:bg-amber-600" onClick={applyDiscount}>
                    <Tag className="w-4 h-4 mr-2" /> Apply Discount
                  </Button>
                </>
              ) : (
                <>
                  <div>
                    <Label>Return Note (optional)</Label>
                    <Input
                      className="mt-1"
                      placeholder="Reason for return..."
                      value={returnNote}
                      onChange={function(e) { setReturnNote(e.target.value); }}
                    />
                  </div>
                  <div className="p-3 bg-blue-50 rounded-lg text-sm">
                    <p className="font-medium text-blue-800 mb-1">Return Summary</p>
                    <p>• Medicine: {actionModal.medicine.brand_name}</p>
                    <p>• Batch: {actionModal.medicine.batch_id}</p>
                    <p>• Quantity: {actionModal.medicine.batch_quantity} units</p>
                    <p>• Value: ₹{actionModal.medicine.potential_loss}</p>
                  </div>
                  <Button className="w-full bg-blue-500 hover:bg-blue-600" onClick={markForReturn}>
                    <RefreshCw className="w-4 h-4 mr-2" /> Submit Return Request
                  </Button>
                </>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default ExpiryDashboard;
